import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle2, Clock, Loader2, RotateCcw } from "lucide-react";

// Query key for the auto-resolve settings — invalidated after set/dismiss.
export const AUTO_RESOLVE_SETTINGS_Q_KEY = ["/api/app/messages/auto-resolve-settings"] as const;

type AutoResolveSettings = {
  cutoff: string | null;
  promptDismissed: boolean;
  lastSweptAt: string | null;
  lastSweptCount: number | null;
};

// Format a UTC ISO string for display in the host's local time.
function formatCutoff(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

// Format the last-swept timestamp as a short date for the activity-log line.
function formatSweptDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

// Convert a Date to the value format required by <input type="datetime-local">
// (YYYY-MM-DDTHH:mm), expressed in local time.
function toDatetimeLocalValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

// Default the picker to the start of the current day in local time.
function defaultPickerValue(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return toDatetimeLocalValue(d);
}

export function AutoResolveDialog({
  open,
  onOpenChange,
  /** When true, show a "Skip for now" dismiss button (first-visit variant). */
  showDismiss,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  showDismiss?: boolean;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const settingsQuery = useQuery<AutoResolveSettings>({
    queryKey: AUTO_RESOLVE_SETTINGS_Q_KEY,
    enabled: open,
  });

  const existingCutoff = settingsQuery.data?.cutoff ?? null;
  const lastSweptAt = settingsQuery.data?.lastSweptAt ?? null;
  const lastSweptCount = settingsQuery.data?.lastSweptCount ?? null;

  // Picker value is a datetime-local string (local time). Initialise to the
  // existing cutoff if one is set, otherwise to start-of-today.
  const [pickerValue, setPickerValue] = useState<string>(defaultPickerValue);
  useEffect(() => {
    if (!settingsQuery.data) return;
    if (settingsQuery.data.cutoff) {
      setPickerValue(toDatetimeLocalValue(new Date(settingsQuery.data.cutoff)));
    } else {
      setPickerValue(defaultPickerValue());
    }
  }, [settingsQuery.data]);

  const setCutoffMutation = useMutation({
    mutationFn: async () => {
      // Parse the local datetime-local input value as a local date then convert
      // to a UTC ISO string for the API.
      const d = new Date(pickerValue);
      if (isNaN(d.getTime())) throw new Error("Please enter a valid date and time.");
      const res = await apiRequest("POST", "/api/app/messages/auto-resolve-settings", {
        cutoff: d.toISOString(),
      });
      return res.json() as Promise<{ cutoff: string; swept: number }>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: AUTO_RESOLVE_SETTINGS_Q_KEY });
      // Inbox counts will drop — invalidate unresolved queries so the
      // sidebar badge and dashboard card refresh immediately.
      queryClient.invalidateQueries({ queryKey: ["/api/app/inbox"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/inbox?unresolvedOnly=1"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/inbox/unresolved-count"] });
      const swept = data.swept;
      toast({
        title: "Auto-resolve cutoff saved",
        description:
          swept > 0
            ? `${swept} message${swept === 1 ? " was" : "s were"} marked resolved.`
            : "Cutoff saved. Future harvested messages older than this date will be auto-resolved.",
      });
      onOpenChange(false);
    },
    onError: (err) => {
      toast({
        title: "Couldn't save cutoff",
        description: parseErrorMessage(err, "Please check the date and try again."),
        variant: "destructive",
      });
    },
  });

  const dismissMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: () =>
      apiRequest("POST", "/api/app/messages/auto-resolve-settings/dismiss"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AUTO_RESOLVE_SETTINGS_Q_KEY });
      onOpenChange(false);
    },
    onError: () => {
      // Dismiss failures are silent — just close the dialog.
      onOpenChange(false);
    },
  });

  const busy = setCutoffMutation.isPending || dismissMutation.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (busy) return;
        onOpenChange(next);
      }}
    >
      <DialogContent
        className="max-w-md"
        data-testid="dialog-auto-resolve"
        onEscapeKeyDown={(e) => { if (busy) e.preventDefault(); }}
        onInteractOutside={(e) => { if (busy) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (busy) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5 text-blue-500" />
            Auto-resolve old messages
          </DialogTitle>
          <DialogDescription>
            Automatically mark all inbound member messages resolved prior to a
            date and time you choose. Use this to clear your inbox of imported
            history without losing genuinely new messages.
          </DialogDescription>
        </DialogHeader>

        {settingsQuery.isLoading ? (
          <div className="flex items-center justify-center py-8 text-gray-400">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="space-y-4">
            {existingCutoff && (
              <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
                <div className="min-w-0 text-sm">
                  <p className="font-medium text-green-800">Active cutoff</p>
                  <p className="mt-0.5 text-green-700">
                    Messages before{" "}
                    <span className="font-medium">{formatCutoff(existingCutoff)}</span>{" "}
                    are auto-resolved.
                  </p>
                </div>
              </div>
            )}

            {lastSweptAt && (
              <p
                className="text-xs text-gray-500"
                data-testid="text-auto-resolve-last-swept"
              >
                Last ran: {formatSweptDate(lastSweptAt)}
                {lastSweptCount != null && (
                  <>
                    {" — "}
                    {lastSweptCount} message{lastSweptCount === 1 ? "" : "s"} resolved
                  </>
                )}
              </p>
            )}

            <div className="space-y-1.5">
              <Label
                htmlFor="auto-resolve-cutoff"
                className="flex items-center gap-1.5 text-sm font-medium text-gray-700"
              >
                <Clock className="h-4 w-4 text-gray-400" />
                Resolve messages older than:
              </Label>
              <Input
                id="auto-resolve-cutoff"
                type="datetime-local"
                value={pickerValue}
                onChange={(e) => setPickerValue(e.target.value)}
                disabled={busy}
                data-testid="input-auto-resolve-cutoff"
              />
              <p className="text-[11px] text-gray-400">
                Times are in your local timezone. Only unresolved inbound messages
                are affected — messages you or a teammate already resolved stay
                resolved regardless.
              </p>
            </div>
          </div>
        )}

        <DialogFooter className="flex-col gap-2 sm:flex-row sm:gap-0">
          {showDismiss && (
            <Button
              variant="ghost"
              onClick={() => dismissMutation.mutate()}
              disabled={busy}
              data-testid="button-auto-resolve-dismiss"
              className="sm:mr-auto"
            >
              {dismissMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Skip for now
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="button-auto-resolve-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={() => setCutoffMutation.mutate()}
            disabled={busy || !pickerValue || settingsQuery.isLoading}
            data-testid="button-auto-resolve-confirm"
          >
            {setCutoffMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…
              </>
            ) : (
              "Save cutoff"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
